/**
 * React hook for Application Insights telemetry.
 *
 * Provides convenient wrappers around the App Insights SDK so components can
 * log events, page views, exceptions, and custom metrics without importing
 * the raw SDK directly.
 *
 * Usage:
 *   const telemetry = useAppInsights();
 *   telemetry.trackEvent('FormSubmitted', { formName: 'manager' });
 *   telemetry.trackException(error);
 */
import { useCallback, useMemo } from 'react';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { appInsights } from '../telemetry/appInsights';

export interface IAppInsightsHook {
  /** Track a named custom event with optional properties & measurements. */
  trackEvent: (
    name: string,
    properties?: Record<string, string>,
    measurements?: Record<string, number>
  ) => void;

  /** Track a page view (usually automatic, but call this for virtual pages). */
  trackPageView: (name?: string, uri?: string) => void;

  /** Track an exception / error. */
  trackException: (error: Error, properties?: Record<string, string>) => void;

  /** Track a custom metric value (e.g. "LoadTime", 1234). */
  trackMetric: (name: string, average: number, properties?: Record<string, string>) => void;

  /** Track a trace / diagnostic message. */
  trackTrace: (
    message: string,
    severityLevel?: SeverityLevel,
    properties?: Record<string, string>
  ) => void;

  /** Set the authenticated user context (call after login). */
  setAuthenticatedUser: (userId: string, accountId?: string) => void;

  /** Clear the authenticated user context (call on logout). */
  clearAuthenticatedUser: () => void;
}

export function useAppInsights(): IAppInsightsHook {
  const trackEvent = useCallback(
    (name: string, properties?: Record<string, string>, measurements?: Record<string, number>) => {
      appInsights.trackEvent({ name }, { ...properties, ...measurements });
    },
    []
  );

  const trackPageView = useCallback((name?: string, uri?: string) => {
    appInsights.trackPageView({ name, uri });
  }, []);

  const trackException = useCallback(
    (error: Error, properties?: Record<string, string>) => {
      appInsights.trackException(
        { exception: error, severityLevel: SeverityLevel.Error },
        properties
      );
    },
    []
  );

  const trackMetric = useCallback(
    (name: string, average: number, properties?: Record<string, string>) => {
      appInsights.trackMetric({ name, average }, properties);
    },
    []
  );

  const trackTrace = useCallback(
    (
      message: string,
      severityLevel: SeverityLevel = SeverityLevel.Information,
      properties?: Record<string, string>
    ) => {
      appInsights.trackTrace({ message, severityLevel }, properties);
    },
    []
  );

  const setAuthenticatedUser = useCallback((userId: string, accountId?: string) => {
    appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }, []);

  const clearAuthenticatedUser = useCallback(() => {
    appInsights.clearAuthenticatedUserContext();
  }, []);

  return useMemo(
    () => ({
      trackEvent,
      trackPageView,
      trackException,
      trackMetric,
      trackTrace,
      setAuthenticatedUser,
      clearAuthenticatedUser,
    }),
    [
      trackEvent,
      trackPageView,
      trackException,
      trackMetric,
      trackTrace,
      setAuthenticatedUser,
      clearAuthenticatedUser,
    ]
  );
}
